// ═══════════════════════════════════════════════════════════════════════
// tenant-brand.ts — the single source of truth for the white-label brand
// name shown anywhere in the Studio / operator-door UI.
// ═══════════════════════════════════════════════════════════════════════
// Priority: the operator's white-label company (resolved from the branded-door
// hostname) → an explicitly-provided OS/workspace name → a neutral fallback.
// This NEVER returns a "Sloe" string — full white-label. Every visible surface
// (sidebar, Explore, the Architect ticker, the document title) reads from here
// instead of hardcoding a fallback.
// ═══════════════════════════════════════════════════════════════════════

import { useMemo } from "react";
import { resolveOperator } from "@/src/data/operators";
import { operatorSlugFromHostname } from "@/src/lib/operator";

export const NEUTRAL_BRAND = "Your Operating System";

export interface TenantBrand {
  name: string;
  shortName: string;
}

function toShortName(name: string): string {
  const n = name.trim();
  return n.length > 12 ? n.slice(0, 12).trim() : n;
}

export function resolveTenantBrand(opts?: { osName?: string | null }): TenantBrand {
  const operator = resolveOperator(operatorSlugFromHostname());
  const company = operator?.company?.trim();
  const osName = opts?.osName?.trim();
  const name = company || osName || NEUTRAL_BRAND;
  return { name, shortName: toShortName(name) };
}

export function useTenantBrand(opts?: { osName?: string | null }): TenantBrand {
  const osName = opts?.osName ?? null;
  return useMemo(() => resolveTenantBrand({ osName }), [osName]);
}
